/** @jsx jsx */
import { Card, jsx } from '@front10/base-elements';
import BasePopover from '@reach/popover';
import {
  forwardRef,
  PropsWithChildren,
  RefObject,
  useCallback,
  useImperativeHandle,
  useState,
  useRef,
  useEffect
} from 'react';

interface Props {
  targetRef: RefObject<HTMLElement>;
  toggleElement: React.ReactNode;
  onClose?: () => void;
  activeTab?: number;
  isOpen?: boolean;
  currentTab?: number;
}

export interface Dropdown {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Dropdown = forwardRef<Dropdown, PropsWithChildren<Props>>(function Dropdown(
  props: PropsWithChildren<Props>,
  ref: ((instance: Dropdown) => void) | RefObject<Dropdown> | null
) {
  const [isOpen, setOpen] = useState(false);
  const onClose = props.onClose;
  const popoverRef = useRef<any>(null);

  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    const func = (event: any) => {
      if (props.currentTab === props.activeTab) {
        if (!popoverRef.current?.contains(event.target)) {
          if (event.target.tagName !== 'INPUT') {
            setOpen(false);
            if (isOpen) {
              props?.onClose?.();
            }
          }
        }
      }
    };
    document.addEventListener('click', func);
    return () => document.removeEventListener('click', func, undefined);
  }, [popoverRef, isOpen, props]);

  const toggle = useCallback(() => {
    setOpen((oldIsOpen) => {
      if (oldIsOpen) {
        onClose?.();
      }
      return !oldIsOpen;
    });
  }, [onClose]);

  useImperativeHandle(ref, () => ({
    open,
    close,
    toggle
  }));

  return (
    <div>
      {props.toggleElement}
      <BasePopover
        sx={{
          display: isOpen ? undefined : 'none',
          variant: 'layout.popover'
        }}
        targetRef={props.targetRef}
      >
        <Card>
          <div ref={popoverRef}>{props.children}</div>
        </Card>
      </BasePopover>
    </div>
  );
});
